import React, { useEffect, useState } from 'react';
import { Image, Skeleton } from 'antd';
import { useAppCustomers } from '@/hooks/useAppCustomers';
import {
  isPrivateImage,
  ProductImageApiError,
  productImages,
} from '@/api/ProductImages/ProductImages';
import { useTranslate } from '@/translations';

export const PrivateImage = ({
  src,
  assetId,
  generationId,
  candidate,
  thumbnail = false,
  label,
}: {
  src?: string | null;
  assetId?: string | null;
  generationId?: string;
  candidate?: number;
  thumbnail?: boolean;
  label?: string;
}) => {
  const { t } = useTranslate();
  const { customerIds, userPermissions, hasGbaAdminAccess } = useAppCustomers();
  const directUrl = src && /^https?:\/\//.test(src) ? src : null;
  const key = JSON.stringify([
    directUrl,
    assetId,
    generationId,
    candidate,
    customerIds,
    userPermissions,
  ]);
  const [image, setImage] = useState<{ key: string; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const canLoad = hasGbaAdminAccess && !!(directUrl || assetId || generationId);
  useEffect(() => {
    let controller = new AbortController();
    let objectUrl: string | null = null;
    let stopped = false;
    const clear = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = null;
      setImage(null);
    };
    const refresh = async () => {
      controller.abort();
      controller = new AbortController();
      const signal = controller.signal;
      clear();
      if (!canLoad || stopped || document.visibilityState === 'hidden') return;
      setLoading(true);
      try {
        if (directUrl) {
          setImage({ key, url: directUrl });
          return;
        }
        const blob = assetId
          ? await productImages.revisionContent(assetId, signal)
          : candidate != null
            ? await productImages.candidateContent(
                generationId!,
                candidate,
                signal,
              )
            : await productImages.generationContent(generationId!, signal);
        if (signal.aborted || !isPrivateImage(blob)) return;
        objectUrl = URL.createObjectURL(blob);
        setImage({ key, url: objectUrl });
      } catch (error) {
        if (signal.aborted) return;
        clear();
        if (
          error instanceof ProductImageApiError &&
          [403, 404].includes(error.status)
        )
          stopped = true;
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };
    void refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        controller.abort();
        clear();
      } else void refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      controller.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      clear();
    };
  }, [assetId, candidate, canLoad, directUrl, generationId, key]);
  return (
    <div className={`imagery-image${thumbnail ? ' imagery-thumbnail' : ''}`}>
      {image?.key === key && hasGbaAdminAccess ? (
        <Image
          src={image.url}
          alt={label ?? t('imagery.generated')}
          preview={!thumbnail}
        />
      ) : loading ? (
        <Skeleton.Image active />
      ) : (
        <span className="imagery-caption">
          {t('imagery.previewUnavailable')}
        </span>
      )}
    </div>
  );
};
